import React from 'react';
import { Checkbox, Flex, Typography, Box } from '@fullstory/components';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { FormattedMessage, useIntl } from 'react-intl';
import { NamedElementSelectorState } from '@fullstory/named-elements';

export const testIds = {
  checkboxWeb: 'multi-platform-select-checkbox-web',
  checkboxIOS: 'multi-platform-select-checkbox-ios',
  checkboxAndroid: 'multi-platform-select-checkbox-android',
};

type MultiPlatformSelectionProps = {
  platforms: Set<NativePlatform>;
  onChange: (platforms: Set<NativePlatform>) => void;
  validationState: NamedElementSelectorState;
  disabled?: boolean;
};

export const MultiPlatformSelection = ({
  platforms,
  onChange,
  validationState,
  disabled,
}: MultiPlatformSelectionProps) => {
  const intl = useIntl();

  const onPlatformChange = (platform: NativePlatform) => () => {
    const updatedPlatforms = new Set(platforms);

    if (platforms.has(platform)) updatedPlatforms.delete(platform);
    else updatedPlatforms.add(platform);
    onChange(updatedPlatforms);
  };

  return (
    <Box data-component="MultiPlatformSelection" minWidth={240}>
      <Flex column alignItems="flex-start">
        <Flex>
          <Box marginRight={20}>
            <Checkbox
              label={intl.formatMessage({ defaultMessage: 'Web', id: 'B6CB37' })}
              checked={platforms.has(NativePlatform.NATIVE_PLATFORM_WEB)}
              onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_WEB)}
              testId={testIds.checkboxWeb}
              disabled={disabled}
            />
          </Box>
          <Box marginRight={20}>
            <Checkbox
              label={intl.formatMessage({ defaultMessage: 'iOS', id: 'OQDKnx' })}
              checked={platforms.has(NativePlatform.NATIVE_PLATFORM_IOS)}
              onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_IOS)}
              testId={testIds.checkboxIOS}
              disabled={disabled}
            />
          </Box>
          <Checkbox
            label={intl.formatMessage({ defaultMessage: 'Android', id: 'gQ7kPU' })}
            checked={platforms.has(NativePlatform.NATIVE_PLATFORM_ANDROID)}
            onChange={onPlatformChange(NativePlatform.NATIVE_PLATFORM_ANDROID)}
            testId={testIds.checkboxAndroid}
            disabled={disabled}
          />
        </Flex>
        {validationState === NamedElementSelectorState.EmptyPlatforms && (
          <Box marginTop={8}>
            <Typography variant="body3" color="red">
              <FormattedMessage
                defaultMessage="At least one platform must be selected"
                id="VPDOfh"
              />
            </Typography>
          </Box>
        )}
      </Flex>
    </Box>
  );
};
